import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ImagePlus, Sparkles, X } from 'lucide-react'
import { NewFolder } from '../../../../Data/album.dto'
import { showErrorToast } from '../../Utlis/Toast'
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
  CardLabel,
  FieldInput,
  PrimaryBtn,
  RequiredStar,
} from '../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles'

interface AddFolderModalProps {
  isOpen: boolean
  onRequestClose: () => void
  onSubmit: (folder: NewFolder) => void
  currentFolder: NewFolder | null
  imageLimit: number
  setCurrentFolder: (data: NewFolder | null) => void
  totalFoldersLength: number
}

const AddFolderModal: React.FC<AddFolderModalProps> = ({
  isOpen,
  onRequestClose,
  onSubmit,
  imageLimit,
  totalFoldersLength,
  currentFolder,
  setCurrentFolder,
}) => {
  const [folderName, setFolderName] = useState('')
  const [newFolderImages, setNewFolderImages] = useState<File[]>([])
  const [folderId, setFolderId] = useState<number>(-1)
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [isUpdate, setIsUpDate] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    if (currentFolder) {
      setIsUpDate(true)
      setFolderName(currentFolder.name)
      setFolderId(currentFolder.id)
      setNewFolderImages(currentFolder.images.map((image) => image.image))
      setImageUrls(currentFolder.images.map((image) => URL.createObjectURL(image.image)))
    } else {
      setFolderName('')
      setFolderId(totalFoldersLength + 1)
      setIsUpDate(false)
      setNewFolderImages([])
      setImageUrls([])
    }

    return () => {
      setCurrentFolder(null)
      imageUrls.forEach((url) => URL.revokeObjectURL(url))
      setImageUrls([])
    }
  }, [isOpen, currentFolder, totalFoldersLength, setCurrentFolder])

  const handleCreateFolder = () => {
    if (!folderName.trim()) {
      showErrorToast('Folder name cannot be empty.')
      return
    }

    const folder: NewFolder = {
      name: folderName,
      id: folderId,
      images: newFolderImages.map((file) => ({
        image: file,
        image_blob: URL.createObjectURL(file),
        media_type: 1,
      })),
    }
    onSubmit(folder)
    onRequestClose()
    setFolderName('')
    setNewFolderImages([])
  }

  if (!isOpen) return null

  const canSubmit = folderName.trim().length > 0

  return (
    <ModalOverlay onClick={onRequestClose}>
      <Shell onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={10} />
                Album folders
              </HeroBadge>
              <ModalTitle>{isUpdate ? 'Update folder' : 'Create folder'}</ModalTitle>
              <ModalSubtitle>
                {isUpdate
                  ? 'Rename this folder for your album organization.'
                  : 'Give your folder a name — you can upload photos after creating the album.'}
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onRequestClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <Body>
          <FormCard>
            <Field>
              <CardLabel>
                Folder name <RequiredStar>*</RequiredStar>
              </CardLabel>
              <FieldInput
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="e.g. Engagement photos"
                autoFocus
              />
            </Field>

            <InfoCard>
              <InfoIcon>
                <ImagePlus size={18} />
              </InfoIcon>
              <InfoText>
                <InfoTitle>Photos upload later</InfoTitle>
                <InfoDesc>
                  {imageLimit === 0
                    ? 'You can upload images after creating the folder.'
                    : `You can upload images after creating the folder.`}
                </InfoDesc>
              </InfoText>
            </InfoCard>
          </FormCard>
        </Body>

        <Footer>
          <CancelBtn type="button" onClick={onRequestClose}>
            Cancel
          </CancelBtn>
          <SubmitBtn type="button" disabled={!canSubmit} onClick={handleCreateFolder}>
            {isUpdate ? 'Update folder' : 'Create folder'}
          </SubmitBtn>
        </Footer>
      </Shell>
    </ModalOverlay>
  )
}

export default AddFolderModal

const Shell = styled(ModalShell)`
  max-width: 520px;
`

const Body = styled(ModalBody)`
  padding-top: 16px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const InfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`

const InfoIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`

const InfoText = styled.div`
  min-width: 0;
`

const InfoTitle = styled.p`
  margin: 0 0 4px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`

const InfoDesc = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  color: rgba(23, 23, 23, 0.55);
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px 24px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
`

const CancelBtn = styled.button`
  flex: 1;
  height: 46px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`

const SubmitBtn = styled(PrimaryBtn)`
  flex: 1;
  width: auto;
`
